/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */

/**
 * The booleanToString function provides a simply utility which converts a given
 * boolean to string.
 *
 * ```typescript
 *
 *   import {booleanToString} from '@ion/core/src/utils/BooleanUtils';
 *
 *   booleanToString(true) // 'Yes'
 *
 * ```
 */
export const booleanToString = (value, yesVal = 'Yes', noVal = 'No') => {
  return value ? yesVal : noVal;
};

/**
 * The stringToBoolean function provides a simply utility which converts a given
 * 'Yes' or 'No' string to boolean.
 *
 * ```typescript
 *
 *   import {stringToBoolean} from '@ion/core/src/utils/';
 *
 *   stringToBoolean('Yes') // true
 *   stringToBoolean('no') // false
 *
 * ```
 */
export const stringToBoolean = (value: string, yesVal = 'yes') => {
  return value.toLowerCase() === yesVal || false;
};

/**
 * Given a value, returns true if the value is the boolean true,
 * or if the value is the string value "true" (case-insensitive).
 *
 * ```typescript
 *
 *   import {isTrue} from '@ion/core/src/utils/BooleanUtils';
 *
 *   isTrue('True') // true
 *   isTrue('true') // true
 *   isTrue('false') // false
 *
 * ```
 */
export const isTrue = (val): boolean => {
  return String(val).toLowerCase() === 'true';
};

/**
 * Given a value, returns true if the value is the boolean false,
 * or if the value is the string value "false" (case-insensitive).
 *
 * ```typescript
 *
 *   import {isFalse} from '@ion/core/src/utils/BooleanUtils';;
 *
 *   isFalse('false') // true
 *   isFalse('FALSE') // true
 *   isFalse('true')  // false
 *
 * ```
 */
export const isFalse = (val): boolean => {
  return String(val).toLowerCase() === 'false';
};

/**
 * The parseToBoolean function provides a simply utility which converts a given
 * 'Yes', 'No', 'true' or 'false' string to boolean.
 *
 * ```typescript
 *
 *   import {parseToBoolean} from '@ion/core/src/utils/';
 *
 *   parseToBoolean('Yes') // true
 *   parseToBoolean('no') // false
 *   parseToBoolean('true') // true
 *   parseToBoolean('false') // false
 *   parseToBoolean(true) // true
 *   parseToBoolean(false) // false
 *   parseToBoolean() // false
 *
 * ```
 */
export const parseToBoolean = (value: string, yesVal = 'yes'): boolean => {
  return isTrue(value) || String(value).toLowerCase() === String(yesVal).toLowerCase();
};

/**
 * Provides a utility which returns `true` if the provided value is a literal
 * string representation of the boolean `true` or `false` values.
 *
 * ```typescript
 *
 *   import {isBooleanString} from '@ion/core/src/utils/';
 *
 *   isBooleanString('true') // true
 *   isBooleanString('TRUE') // true
 *   isBooleanString('false') // true
 *   isBooleanString('FALSE') // true
 *   isBooleanString('no-boolean-string') // false
 *
 * ```
 */
export const isBooleanString = (val: string): boolean => (
  isTrue(val) || isFalse(val)
);

/**
 * Provides a utility which converts a string representation of boolean `true`
 * or boolean `false` (case-insensitive) to the boolean literal value `true`
 * or `false`, otherwise, returns `undefined`.
 *
 * ```typescript
 *
 *   import {fromBooleanString} from '@ion/core/src/utils/';
 *
 *   fromBooleanString('true') // true
 *   fromBooleanString('TRUE') // true
 *   fromBooleanString('false') // false
 *   fromBooleanString('FALSE') // true
 *   fromBooleanString('no-boolean-string') // undefined
 *
 * ```
 */
export const fromBooleanString = (val: string): boolean | undefined => {
  if (isBooleanString(val)) {
    return isTrue(val);
  }
};

/**
 * The booleanToSwitchString function provides a simply utility which converts a given
 * boolean to On/Off string.
 *
 * ```typescript
 *
 *   import {booleanToSwitchString} from '@ion/core/src/utils/';
 *
 *   booleanToSwitchString(true) // 'On'
 *   booleanToSwitchString(false) // 'Off'
 *
 * ```
 */
export const booleanToSwitchString = (value: string | boolean, onVal = 'On', offVal = 'Off') => {
  return isTrue(value) ? onVal : offVal;
};
